import { useCallback, useMemo } from 'react';
import type { ReportsQueryScope } from '../../domain/reportsQueryScope';
import {
  getReportsDateRange,
} from '../reportsScreen.utils';
import type { DateRangePreset, ReportsSection } from '../reportsScreen.constants';
import { createReportsShareActions } from '../reportsScreenShareActions';
import { useReportsDashboardLoad } from './useReportsDashboardLoad';
import { useReportsPersonalPane } from './useReportsPersonalPane';
import { useReportsExpensesPane } from './useReportsExpensesPane';
import { useReportsTreasuryPane } from './useReportsTreasuryPane';
import type { CatalogItemTypeFilter } from '../../../catalog/domain-api';
import type { BranchSalesByProductRow } from '../../domain/reportsTypes';
import type { SalesByProductSortKey } from '../../domain/reportsTypes';
import type { PurchasesSummaryReport } from '../../domain/reportsPurchasesTypes';
import type { SupplierPerformanceRow } from '../../domain/reportsSupplierPerformanceTypes';

type Params = {
  currentBranchId: string | null | undefined;
  userId: string | null | undefined;
  canEdit: boolean;
  canExport?: boolean;
  dateRangePreset: DateRangePreset;
  customRangeFrom: Date;
  customRangeTo: Date;
  reportsQueryScope: ReportsQueryScope;
  customerModuleEnabled: boolean;
  mercadopagoQrMethodAllowed: boolean;
  shouldLoadOperacionDashboard: boolean;
  shouldLoadIntelligence: boolean;
  shouldLoadPersonalDashboard: boolean;
  shouldLoadTreasurySummary: boolean;
  reportsBootstrapReady: boolean;
  activeSection: ReportsSection;
  salesByProductCategory: string | null;
  salesByProductItemTypeFilter: CatalogItemTypeFilter;
  reportsVentasPinnedProductId: string | null;
  reportsVentasPinnedProductLabel: string | null;
  salesByProductSort: SalesByProductSortKey;
  salesByProductRows: BranchSalesByProductRow[];
  salesByProductPage: number;
  salesByProductPageSize: number;
  salesByProductTotalCount: number;
  salesByProductSumQuantity: number;
  salesByProductSumLineTotal: number;
  salesByProductUniformUnitOfMeasure: boolean;
  bumpSalesByProductRefreshKey: () => void;
  purchasesSummary?: PurchasesSummaryReport | null;
  supplierPerformance?: SupplierPerformanceRow[];
};

export function useReportsOperacionPane({
  currentBranchId,
  userId,
  canEdit,
  canExport = true,
  dateRangePreset,
  customRangeFrom,
  customRangeTo,
  reportsQueryScope,
  customerModuleEnabled,
  mercadopagoQrMethodAllowed,
  shouldLoadOperacionDashboard,
  shouldLoadIntelligence,
  shouldLoadPersonalDashboard,
  shouldLoadTreasurySummary,
  reportsBootstrapReady,
  activeSection,
  salesByProductCategory,
  salesByProductItemTypeFilter,
  reportsVentasPinnedProductId,
  reportsVentasPinnedProductLabel,
  salesByProductSort,
  salesByProductRows,
  salesByProductPage,
  salesByProductPageSize,
  salesByProductTotalCount,
  salesByProductSumQuantity,
  salesByProductSumLineTotal,
  salesByProductUniformUnitOfMeasure,
  bumpSalesByProductRefreshKey,
  purchasesSummary,
  supplierPerformance,
}: Params) {
  const dashboardCacheKey = useMemo(
    () =>
      `${currentBranchId ?? ''}|${dateRangePreset}|${customRangeFrom.getTime()}|${customRangeTo.getTime()}|${JSON.stringify(reportsQueryScope)}|${userId ?? ''}|${customerModuleEnabled}|${mercadopagoQrMethodAllowed}`,
    [
      currentBranchId,
      dateRangePreset,
      customRangeFrom,
      customRangeTo,
      reportsQueryScope,
      userId,
      customerModuleEnabled,
      mercadopagoQrMethodAllowed,
    ]
  );

  const dashboard = useReportsDashboardLoad({
    currentBranchId,
    userId,
    dateRangePreset,
    customRangeFrom,
    customRangeTo,
    dashboardCacheKey,
    reportsQueryScope,
    customerModuleEnabled,
    mercadopagoQrMethodAllowed,
    shouldLoadOperacionDashboard,
    shouldLoadIntelligence,
    reportsBootstrapReady,
  });

  const {
    cashSummary,
    todaySalesSnapshot,
    periodSalesSnapshot,
    previousPeriodSalesSnapshot,
    salesDailySeries,
    posExceptionSummary,
    inventoryDashboard,
    salesComparison,
    timeBands,
    salesByCashier,
    promotionPerformance,
    customerSummary,
    loading,
    loadError,
    lastDashboardLoadAt,
    paymentMethodNames,
    externalServiceSummary,
    kpiSources,
    branchCashSessions,
    salesByCashierSource,
    periodRentabilidadKpis,
    previousPeriodRentabilidadKpis,
    rentabilidadLoading,
    rentabilidadError,
    rentabilidadErrorShowRetry,
    intelligenceData,
    intelligenceLoading,
    intelligenceError,
    intelligenceErrorShowRetry,
    branchHealth,
    salesFiscalCount,
    salesFiscalTotal,
    salesNonFiscalCount,
    salesNonFiscalTotal,
    todaySalesFiscalCount,
    todaySalesFiscalTotal,
    todaySalesNonFiscalCount,
    todaySalesNonFiscalTotal,
    setExternalServiceSummary,
    loadDashboard,
    loadOperacionRentabilidad,
    loadIntelligence,
    clearDashboardCache,
  } = dashboard;

  const {
    personalDashboard,
    personalOperatorsForRanking,
    personalLoading,
    personalLoadError,
    reloadPersonalDashboard,
  } = useReportsPersonalPane({
    currentBranchId,
    userId,
    dateRangePreset,
    customRangeFrom,
    customRangeTo,
    reportsQueryScope,
    shouldLoadPersonalDashboard,
  });

  const {
    treasurySummary,
    treasuryLoading,
    treasuryError,
    reloadTreasury,
  } = useReportsTreasuryPane({
    currentBranchId,
    dateRangePreset,
    customRangeFrom,
    customRangeTo,
    shouldLoad: shouldLoadTreasurySummary,
  });

  const {
    expensesSummary,
    profitLoss,
    expensesProfitLossLoading,
  } = useReportsExpensesPane({
    currentBranchId,
    dateRangePreset,
    customRangeFrom,
    customRangeTo,
    cashSessionOutflowsTotal: cashSummary?.sessionOutflowsTotal,
  });

  const loadFn = useCallback(async (opts?: { force?: boolean }) => {
    await loadDashboard(true);
    if (shouldLoadOperacionDashboard) {
      await loadOperacionRentabilidad();
    }
    await reloadPersonalDashboard();
    await reloadTreasury({ force: true });
    if (activeSection === 'operacion') {
      await loadIntelligence();
    }
    bumpSalesByProductRefreshKey();
  }, [
    loadDashboard,
    shouldLoadOperacionDashboard,
    loadOperacionRentabilidad,
    reloadPersonalDashboard,
    reloadTreasury,
    activeSection,
    loadIntelligence,
    bumpSalesByProductRefreshKey,
  ]);

  const {
    handleShareSummary,
    handleShareSalesByProduct,
    handleShareVentasSummary,
    handleSharePersonalRanking,
    handleShareCompras,
  } = createReportsShareActions({
    dateRangePreset,
    customRangeFrom,
    customRangeTo,
    todaySalesSnapshot,
    periodSalesSnapshot,
    periodRentabilidadKpis,
    inventoryDashboard,
    cashSummary,
    expensesSummary,
    profitLoss,
    salesByProductCategory,
    salesByProductItemTypeFilter,
    reportsVentasPinnedProductId,
    reportsVentasPinnedProductLabel,
    salesByProductSort,
    salesByProductRows,
    salesByProductPage,
    salesByProductPageSize,
    salesByProductTotalCount,
    salesByProductSumQuantity,
    salesByProductSumLineTotal,
    salesByProductUniformUnitOfMeasure,
    personalOperatorsForRanking,
    salesByCashier,
    currentBranchId: currentBranchId ?? null,
    canEdit,
    canExport,
    purchasesSummary,
    supplierPerformance,
    treasurySummary,
  });

  return {
    clearDashboardCache,
    personalDashboard,
    personalOperatorsForRanking,
    personalLoading,
    personalLoadError,
    loading,
    loadError,
    loadFn,
    lastDashboardLoadAt,
    branchHealth,
    cashSummary,
    todaySalesSnapshot,
    periodSalesSnapshot,
    previousPeriodSalesSnapshot,
    salesDailySeries,
    posExceptionSummary,
    inventoryDashboard,
    salesComparison,
    timeBands,
    salesByCashier,
    promotionPerformance,
    salesByCashierSource,
    customerSummary,
    paymentMethodNames,
    externalServiceSummary,
    intelligenceData,
    intelligenceLoading,
    intelligenceError,
    intelligenceErrorShowRetry,
    handleLoadIntelligence: loadIntelligence,
    handleShareSummary,
    handleShareSalesByProduct,
    handleShareVentasSummary,
    handleSharePersonalRanking,
    handleShareCompras,
    kpiSources,
    branchCashSessions,
    periodRentabilidadKpis,
    previousPeriodRentabilidadKpis,
    rentabilidadLoading,
    rentabilidadError,
    rentabilidadErrorShowRetry,
    handleLoadRentabilidad: loadOperacionRentabilidad,
    salesFiscalCount,
    salesFiscalTotal,
    salesNonFiscalCount,
    salesNonFiscalTotal,
    todaySalesFiscalCount,
    todaySalesFiscalTotal,
    todaySalesNonFiscalCount,
    todaySalesNonFiscalTotal,
    expensesSummary,
    profitLoss,
    expensesProfitLossLoading,
    treasurySummary,
    treasuryLoading,
    treasuryError,
    reloadTreasury,
  };
}
